import { STAGE_STATE_LABEL, type Shot } from '@ai-studio/shared';

import { rawUrl } from '../../lib/thumbnails.js';
import { addScene, addShot, audioBadges, moveShot, overallState, removeShot, shotBadges, shotThumb, timecode, updateScene, type Badge } from '../../lib/video.js';
import { useVideo } from '../../state/video.js';
import { useWorkspace } from '../../state/workspace.js';
import { IconButton, MenuButton, TextField, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

function Badges({ badges }: { badges: readonly Badge[] }) {
  return (
    <span className="vs-badges">
      {badges.map((b) => (
        <span key={b.key} className={`vs-dot is-${b.state}`} title={`${b.label}: ${STAGE_STATE_LABEL[b.state]}`}>
          {b.short}
        </span>
      ))}
    </span>
  );
}

/** Regenerate / render actions of one shot. */
export function shotMenu(shot: Shot): MenuItem[] {
  const s = useVideo.getState();
  const regen = (stage: string, group: string) => () => void s.render({ groups: [group as never], shot: shot.id, keys: [`shot:${shot.id}:${stage}`] });
  const items: MenuItem[] = [{ id: 'h', label: shot.id, heading: true }];
  if (shot.type === 'engine') {
    items.push({ id: 'engine', label: 'Record engine shot', icon: 'camera', run: regen('engine', 'engine') });
  } else {
    items.push(
      { id: 'kf', label: 'Regenerate keyframe', icon: 'image', run: regen('keyframe', 'keyframes') },
      { id: 'an', label: 'Regenerate animatic', icon: 'frames', run: regen('animatic', 'animatic') },
      { id: 'fi', label: 'Regenerate final', icon: 'camera', run: regen('final', 'final') },
      { id: 'po', label: 'Redo post (interpolate / upscale)', icon: 'wand', run: regen('post', 'post') },
    );
  }
  items.push(
    { id: 'all', label: 'Render this shot (final pass)', icon: 'play', run: () => void s.render({ preset: 'final', shot: shot.id }) },
    { id: 'audio', label: 'Render its voices and sounds', icon: 'audio', run: () => void s.render({ groups: ['voice', 'lipsync', 'sfx'], shot: shot.id }) },
    { id: 'sep', label: '', separator: true },
    { id: 'up', label: 'Move earlier', icon: 'arrowUp', run: () => s.edit((d) => moveShot(d, shot.id, -1)) },
    { id: 'down', label: 'Move later', icon: 'arrowDown', run: () => s.edit((d) => moveShot(d, shot.id, 1)) },
    { id: 'del', label: 'Delete shot', icon: 'trash', danger: true, run: () => s.edit((d) => removeShot(d, shot.id)) },
  );
  return items;
}

function ShotCard({ shot, start }: { shot: Shot; start: number }) {
  const projectId = useWorkspace((s) => s.projectId);
  const pieces = useVideo((s) => s.pieces);
  const dir = useVideo((s) => s.outputDir);
  const version = useVideo((s) => s.version);
  const selected = useVideo((s) => s.selection.kind === 'shot' && s.selection.id === shot.id);
  const badges = shotBadges(shot, pieces);
  const audio = audioBadges(shot, pieces);
  const state = overallState([...badges, ...audio]);
  const thumb = shotThumb(dir, shot, pieces);
  const s = useVideo.getState();
  return (
    <div
      className={`vs-shot is-${state}${selected ? ' is-selected' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => {
        s.select({ kind: 'shot', id: shot.id });
        s.setTime(start);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') s.select({ kind: 'shot', id: shot.id });
      }}
    >
      <div className="vs-shot-thumb">
        {thumb && projectId ? <img src={rawUrl(projectId, thumb, version)} alt="" loading="lazy" /> : <Icon name={shot.type === 'engine' ? 'scene' : 'image'} size={20} />}
        <span className="vs-shot-time">{timecode(start)}</span>
        <span className={`vs-shot-type is-${shot.type}`}>{shot.type === 'engine' ? 'Engine' : 'AI'}</span>
      </div>
      <div className="vs-shot-body">
        <div className="vs-row">
          <strong className="vs-shot-title">{shot.title || shot.id}</strong>
          <span className="muted">{shot.duration}s</span>
          <span className="bar-spacer" />
          <span onClick={(e) => e.stopPropagation()}>
            <MenuButton className="ibtn" title="Shot actions" items={() => shotMenu(shot)}>
              <Icon name="more" size={13} />
            </MenuButton>
          </span>
        </div>
        <p className="vs-shot-desc" title={shot.description}>
          {shot.description || <span className="muted">No description</span>}
        </p>
        <div className="vs-row">
          <Badges badges={badges} />
          {audio.length > 0 && <Badges badges={audio} />}
        </div>
      </div>
    </div>
  );
}

export function Storyboard() {
  const doc = useVideo((s) => s.doc);
  if (!doc) return null;
  const s = useVideo.getState();
  const { entries } = s.timeline();
  const startOf = new Map(entries.map((e) => [e.shotId, e.start]));
  return (
    <div className="vs-board">
      {doc.acts.length === 0 && (
        <div className="pane-empty muted">
          No scenes yet. Use <strong>Plan with Assistant</strong> above, or add a scene.
        </div>
      )}
      {doc.acts.map((act) => (
        <section key={act.id} className="vs-act">
          <h4 className="vs-act-title">
            {act.title} <code className="muted">{act.id}</code>
          </h4>
          {act.scenes.map((scene) => (
            <div key={scene.id} className="vs-scene">
              <div className="vs-scene-head">
                <Icon name="frames" size={13} />
                <TextField value={scene.title} onChange={(title) => s.edit((d) => updateScene(d, scene.id, { title }))} />
                <code className="muted">{scene.id}</code>
                {scene.location && <span className="muted">@{scene.location}</span>}
                <span className="bar-spacer" />
                <button type="button" className="ghost small" title="Render this scene's animatic" onClick={() => void s.render({ preset: 'animatic', scene: scene.id })}>
                  <Icon name="frames" size={12} /> Animatic
                </button>
                <button type="button" className="ghost small" title="Render this scene's final shots and audio" onClick={() => void s.render({ preset: 'final', scene: scene.id })}>
                  <Icon name="camera" size={12} /> Final
                </button>
                <IconButton
                  icon="plus"
                  size={13}
                  title="Add a shot at the end of this scene"
                  onClick={() => {
                    const r = addShot(useVideo.getState().doc!, scene.id);
                    s.edit(() => r.doc);
                    s.select({ kind: 'shot', id: r.id });
                  }}
                />
              </div>
              {scene.synopsis && <p className="muted vs-scene-syn">{scene.synopsis}</p>}
              <div className="vs-grid">
                {scene.shots.map((shot) => (
                  <ShotCard key={shot.id} shot={shot} start={startOf.get(shot.id) ?? 0} />
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
      <button
        type="button"
        className="ghost small vs-add-scene"
        onClick={() => {
          const r = addScene(useVideo.getState().doc!);
          s.edit(() => r.doc);
        }}
      >
        <Icon name="plus" size={12} /> Add scene
      </button>
    </div>
  );
}
